import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  useTickets,
  useAcceptTicket,
  useReleaseTicket,
  type RecordingTicket,
} from '../hooks/useTickets';
import { useAuth } from '../hooks/useAuth';
import {
  isAdmin,
  isCustomerSupport,
  isVideoRecorder,
  normalizeStatus,
} from '../utils/roles';
import { userDisplayName, formatSlot } from '../utils/ticketDisplay';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import TicketStatusBadge from '../components/TicketStatusBadge';
import { Video, Calendar, User, CheckCircle, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const QUEUE_STATUSES = ['open', 'accepted', 'recording', 'processing', 'completed'] as const;

function canRecordTicket(ticket: RecordingTicket, userId?: string) {
  const s = normalizeStatus(ticket.status);
  if (s === 'open') return true;
  if ((s === 'accepted' || s === 'recording') && ticket.assignedTo?.id === userId) return true;
  return false;
}

function TicketCard({
  ticket,
  userId,
  userRole,
  onAcceptAndRecord,
  onRelease,
}: {
  ticket: RecordingTicket;
  userId?: string;
  userRole?: string;
  onAcceptAndRecord: (id: string) => void;
  onRelease: (id: string) => void;
}) {
  const status = normalizeStatus(ticket.status);
  const showRecord = !isCustomerSupport(userRole) && canRecordTicket(ticket, userId);

  return (
    <div className="etr-surface p-6 border border-gray-200">
      <TicketCardHeader ticket={ticket} />
      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-2 mb-4">
        {ticket.slotStartAt && (
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {formatSlot(ticket.slotStartAt)}
          </span>
        )}
        {ticket.requestedBy && (
          <span className="flex items-center gap-1">
            <User className="h-4 w-4" />
            {userDisplayName(ticket.requestedBy)}
          </span>
        )}
        {ticket.assignedTo && (
          <span className="text-gray-600">Assigned: {userDisplayName(ticket.assignedTo)}</span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {status === 'open' && showRecord && (
          <Button variant="primary" leftIcon={<Video className="h-4 w-4" />} onClick={() => onAcceptAndRecord(ticket.id)}>
            Accept &amp; record
          </Button>
        )}
        {(status === 'accepted' || status === 'recording') && ticket.assignedTo?.id === userId && (
          <>
            <Link to={`/sessions/new?ticketId=${ticket.id}`}>
              <Button variant="primary" leftIcon={<Video className="h-4 w-4" />}>
                {status === 'recording' ? 'Continue recording' : 'Start recording'}
              </Button>
            </Link>
            {status === 'accepted' && (
              <Button variant="outline" onClick={() => onRelease(ticket.id)}>
                Release
              </Button>
            )}
          </>
        )}
        {ticket.sessionId && (
          <Link to={`/sessions/${ticket.sessionId}`}>
            <Button variant="outline" size="sm" leftIcon={<CheckCircle className="h-4 w-4" />}>
              View session
            </Button>
          </Link>
        )}
        <Link to={`/tickets/${ticket.id}`}>
          <Button variant="outline" size="sm" leftIcon={<ArrowRight className="h-4 w-4" />}>
            Details
          </Button>
        </Link>
      </div>
    </div>
  );
}

function TicketCardHeader({ ticket }: { ticket: RecordingTicket }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h3 className="text-lg font-semibold text-gray-900">{ticket.title}</h3>
      {ticket.ticketNumber && (
        <span className="text-xs font-mono text-gray-500">{ticket.ticketNumber}</span>
      )}
      <TicketStatusBadge status={ticket.status} />
    </div>
  );
}

const MyTickets: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useTickets(undefined, { poll: true });
  const acceptMutation = useAcceptTicket();
  const releaseMutation = useReleaseTicket();

  const tickets = data?.tickets ?? [];
  const userId = user?.id;

  const visible = tickets.filter((t) => {
    if (isAdmin(user?.role) || isVideoRecorder(user?.role)) {
      const s = normalizeStatus(t.status);
      if (s === 'open') return true;
      return t.assignedTo?.id === userId;
    }
    if (isCustomerSupport(user?.role)) {
      return t.requestedBy?.id === userId;
    }
    return t.assignedTo?.id === userId;
  });

  const grouped = QUEUE_STATUSES.map((key) => ({
    key,
    items: visible.filter((t) => normalizeStatus(t.status) === key),
  })).filter((g) => g.items.length > 0);

  const handleAcceptAndRecord = async (id: string) => {
    try {
      await acceptMutation.mutateAsync(id);
      navigate(`/sessions/new?ticketId=${id}`);
    } catch {
      toast.error('Could not accept ticket');
    }
  };

  const handleRelease = (id: string) => {
    if (window.confirm('Release this ticket back to the queue?')) {
      releaseMutation.mutate(id);
    }
  };

  return (
    <div className="etr-page">
      <div className="etr-page-header">
        <h1 className="etr-page-title mb-2">My tickets</h1>
        <p className="etr-page-subtitle">
          {isCustomerSupport(user?.role)
            ? 'Tickets you created for recording.'
            : 'Your recording queue — accept open tickets, then record.'}
        </p>
      </div>

      {isLoading ? (
        <Center>
          <LoadingSpinner size="lg" />
        </Center>
      ) : visible.length === 0 ? (
        <div className="etr-surface p-12 text-center text-gray-500">No tickets in your queue.</div>
      ) : (
        <div className="space-y-8">
          {grouped.map((group) => (
            <section key={group.key}>
              <h2 className="text-lg font-semibold text-gray-900 mb-3 capitalize">{group.key}</h2>
              <div className="space-y-4">
                {group.items.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    userId={userId}
                    userRole={user?.role}
                    onAcceptAndRecord={handleAcceptAndRecord}
                    onRelease={handleRelease}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

function Center({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-center py-12">{children}</div>;
}

export default MyTickets;
